<template>
  <div class="reports__data mt-8">
    <v-data-table
      class="data__table sales-table"
      ref="table"
      :headers="headers"
      :items="filteredItems"
      fixed-header
      hide-default-footer
      disable-pagination
      show-select
      disable-sort
      :item-key="itemKey"
      :no-data-text="emptyText"
      :mobile-breakpoint="mobileBreakpoint"
      v-model="selected"
      @input="emitSelection"
    >
      <template v-slot:item.location_name="{ item }">
        <span>{{ item.location_name || '-' }}</span>
      </template>
      <template v-slot:item.location_code="{ item }">
        <span>{{ item.location_code || '-' }}</span>
      </template>
      <template v-slot:item.num_parkings="{ item }">
        <span>{{ displayValue(item.num_parkings) }}</span>
      </template>
      <template v-slot:item.price_with_vat="{ item }">
        <span>{{ formatMoney(item.price_with_vat, item) }}</span>
      </template>
      <template v-slot:item.vat_amount="{ item }">
        <span>{{ formatMoney(item.vat_amount, item) }}</span>
      </template>
      <template v-slot:item.vat_percentage="{ item }">
        <span>{{ formatPercent(item.vat_percentage) }}</span>
      </template>
      <template v-slot:item.discount_amount="{ item }">
        <span>{{ formatMoney(item.discount_amount, item) }}</span>
      </template>
      <template v-slot:item.credit_amount="{ item }">
        <span>{{ formatMoney(item.credit_amount, item) }}</span>
      </template>
      <template v-slot:item.total="{ item }">
        <span>{{ formatMoney(item.total, item) }}</span>
      </template>
    </v-data-table>
  </div>
</template>

<script>
export default {
  name: 'DataTableSales',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    search: {
      type: String,
      default: ''
    },
    searchBy: {
      type: String,
      default: 'location_name'
    }
  },
  data() {
    return {
      selected: [],
      itemKey: 'location_id',
      headers: [
        { text: 'Location name', value: 'location_name' },
        { text: 'Location code', value: 'location_code' },
        { text: 'Number of parkings', value: 'num_parkings' },
        { text: 'Price inc. VAT', value: 'price_with_vat' },
        { text: 'VAT', value: 'vat_amount' },
        { text: 'VAT %', value: 'vat_percentage' },
        { text: 'Discount', value: 'discount_amount' },
        { text: 'Credits', value: 'credit_amount' },
        { text: 'Total sum', value: 'total' }
      ],
      mobileBreakpoint: 767 // screen width in pixel to switch to mobile view
    }
  },
  computed: {
    isMobile() {
      return window.innerWidth <= this.mobileBreakpoint
    },
    filteredItems() {
      const source = this.data || []
      const query = (this.search || '').toString().trim().toLowerCase()
      if (!query) {
        return source
      }
      const key = this.searchBy || 'location_name'
      return source.filter((item) => {
        const raw = item ? item[key] : null
        if (raw === null || raw === undefined) {
          return false
        }
        return raw.toString().toLowerCase().includes(query)
      })
    },
    // The table always receives the full data set, so Vuetify cannot tell an
    // empty period apart from a search that matched nothing - do it here
    emptyText() {
      return (this.data || []).length === 0
        ? this.$t('sales_no_data')
        : this.$t('sales_no_search_results')
    }
  },
  watch: {
    // Selections hold item objects, and Vuetify never prunes them when the
    // items change. Re-point them at the freshly fetched rows and drop the
    // locations that are no longer part of the data set.
    data(newData) {
      if (!this.selected.length) {
        return
      }
      const currentItems = new Map(
        (newData || []).map((item) => [item[this.itemKey], item])
      )
      const synced = this.selected
        .map((item) => currentItems.get(item[this.itemKey]))
        .filter((item) => item !== undefined)

      const isUnchanged =
        synced.length === this.selected.length &&
        synced.every((item, index) => item === this.selected[index])
      if (isUnchanged) {
        return
      }

      this.selected = synced
      this.emitSelection(synced)
    }
  },
  methods: {
    emitSelection(selected) {
      this.$emit('selectionChanged', selected)
    },
    displayValue(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      return value
    },
    formatNumber(value, decimals = 2) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      if (typeof value === 'string') {
        return value
      }
      const fixed = Number(value).toFixed(decimals)
      return fixed.replace('.', ',')
    },
    formatMoney(value, item) {
      const formatted = this.formatNumber(value, 2)
      if (formatted === '-') {
        return formatted
      }
      const currency = item?.currency_symbol || item?.currency
      return currency ? `${formatted} ${currency}` : formatted
    },
    formatPercent(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      if (typeof value === 'string') {
        return value.includes('%') ? value : `${value} %`
      }
      return `${this.formatNumber(value, 2)} %`
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep th {
  text-wrap: nowrap;
}
</style>
